import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { useAuth } from '../auth';
import type { ActivitiesView } from '../../components/dashboard/activity-row/activityView';
import type { UserSwitchPrefs } from './rollout';

/**
 * Preferenze della Dashboard per utente, salvate nel profilo (user_profiles.section_hints_state,
 * chiave `dashboard_prefs`: lo stesso campo JSON dei suggerimenti per sezione, nessuna colonna nuova).
 * Valgono su ogni dispositivo dell'utente.
 */
export interface DashboardPrefs extends UserSwitchPrefs {
  /** Benvenuto della nuova Dashboard fatto (scelta da dove partire). */
  welcome_done_at?: string;
  /** "Tre cose da provare" chiuso. */
  try_dismissed?: boolean;
  /** Motivi scelti tornando alla Dashboard di prima (li vede l'admin; il testo libero va solo a Obly). */
  back_reasons?: string[];
  activities_mode?: 'meta' | 'tutto' | 'lato';
  activities_layout?: 'list' | 'kanban';
  /** Vista del blocco attivita' (compatta, cosa mostrare, fino a quando). */
  activities_view?: ActivitiesView;
  /** Dashboard messe nelle schede in alto (puntina), nell'ordine scelto. */
  favorites?: string[];
  /** Dashboard scelta con "Imposta come predefinita": la prima scheda e quella che si apre. */
  default_page?: string;
  /** Giro guidato visto o saltato. */
  tour_seen_at?: string;
  /**
   * Riepilogo email del mattino (lo invia il core, job "dashboard-digest"): i numeri della dashboard scelta.
   * `last_sent_day` lo scrive il core (YYYY-MM-DD, Europe/Rome) per non mandarlo due volte.
   */
  digest?: DigestPrefs;
}

export interface DigestPrefs {
  enabled: boolean;
  /** Dashboard da cui prendere i numeri (id o home:<modello>). */
  dashboard_id: string;
  /** Ora di invio, Europe/Rome (6-12). */
  hour: number;
  /** Giorni della settimana, 1 = lunedi ... 7 = domenica. */
  days: number[];
  /** Periodo dei numeri: ieri, da inizio mese o da inizio settimana. */
  period: 'yesterday' | 'this_month' | 'this_week';
  last_sent_day?: string;
}

const KEY = 'dashboard_prefs';

/** Modifiche non ancora ricaricate dal profilo, condivise da tutti i componenti della pagina. */
const useLocalPrefs = create<{ uid: string | null; prefs: DashboardPrefs; merge: (uid: string | null, p: DashboardPrefs) => void }>(set => ({
  uid: null,
  prefs: {},
  // Cambio di utente (logout/login): si riparte da zero
  merge: (uid, p) => set(s => ({ uid, prefs: s.uid === uid ? { ...s.prefs, ...p } : { ...p } })),
}));
const EMPTY: DashboardPrefs = {};

export function useDashboardPrefs() {
  const { user, userProfile } = useAuth();
  const stored = (userProfile as { section_hints_state?: Record<string, unknown> } | null)?.section_hints_state;
  const fromProfile = useMemo(() => ((stored?.[KEY] as DashboardPrefs | undefined) ?? {}), [stored]);
  // Le modifiche si vedono subito in tutti i componenti (stato condiviso); il profilo nel contesto
  // si riallinea al prossimo caricamento
  const local = useLocalPrefs(s => (s.uid === (user?.id ?? null) ? s.prefs : EMPTY));
  const setLocal = useLocalPrefs(s => s.merge);
  const prefs = useMemo(() => ({ ...fromProfile, ...local }), [fromProfile, local]);

  const save = useCallback(async (patch: DashboardPrefs) => {
    setLocal(user?.id ?? null, patch);
    if (!user?.id) return;
    // Rilegge lo stato attuale per non cancellare i suggerimenti delle altre sezioni
    const { data } = await supabase.from('user_profiles').select('section_hints_state').eq('id', user.id).maybeSingle();
    const all = ((data as { section_hints_state?: Record<string, unknown> } | null)?.section_hints_state) ?? {};
    const next = { ...all, [KEY]: { ...((all[KEY] as DashboardPrefs) ?? {}), ...patch } };
    const { error } = await supabase.from('user_profiles').update({ section_hints_state: next }).eq('id', user.id);
    if (error) console.error('[Dashboard] preferenze', error);
  }, [user?.id, setLocal]);

  return { prefs, save, ready: !!userProfile };
}
